import React from 'react';

import { IconButton } from '@rbilabs/universal-components';
import { ICartEntry } from 'features/cart/types';

import { FavoriteIcon } from 'components/favorite-icon';
import { toaster } from 'components/toaster/toaster';
import {
  GetUserFavoritesRefsDocument,
  GetUserMinimalFavoritesDocument,
  IMenuSelectionInput,
  useCreateMinimalFavoriteMutation,
  useDeleteMinimalFavoriteMutation,
  useGetUserMinimalFavoritesQuery,
} from 'generated/graphql-gateway';
import { useFavoritesContext } from 'state/favorites';
import { t } from 'state/intl/translations';
import { getMinimalCartEntryRef } from 'utils/menu/get-cart-entry-ref';

type FavoriteEntryNode = {
  id: string;
  menuSelections?: ReadonlyArray<FavoriteEntryNode> | null;
};

const containsEntryId = (
  entries: ReadonlyArray<FavoriteEntryNode> = [],
  itemId: string
): boolean => {
  return entries.some(entry => {
    if (entry.id === itemId) {
      return true;
    }

    return containsEntryId(entry.menuSelections ?? [], itemId);
  });
};

const mapMenuSelections = (
  selections: readonly IMenuSelectionInput[] | null | undefined
): IMenuSelectionInput[] => {
  if (!selections?.length) {
    return [];
  }

  return selections.map(selection => ({
    id: selection.id,
    quantity: selection.quantity,
    menuSelections: mapMenuSelections(selection.menuSelections),
  }));
};

export const MinimalRecentEntryFavoriteButton = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const { setShouldRefetchFavorites } = useFavoritesContext();
  const { data } = useGetUserMinimalFavoritesQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });
  const [createMinimalFavorite, { loading: createLoading }] = useCreateMinimalFavoriteMutation();
  const [deleteMinimalFavorite, { loading: deleteLoading }] = useDeleteMinimalFavoriteMutation();

  const favoriteMatch = data?.userMinimalFavorites?.favorites?.find(favorite =>
    containsEntryId(
      (favorite?.minimalFavoriteEntries as ReadonlyArray<FavoriteEntryNode>) ?? [],
      cartEntry.id
    )
  );

  const isFavorite = !!favoriteMatch;
  const loading = createLoading || deleteLoading;
  const accessibilityLabel = isFavorite ? t('removeFavorite') : t('addFavorite');

  const onPress = () => {
    if (isFavorite && favoriteMatch?._id) {
      deleteMinimalFavorite({
        variables: {
          favoriteId: favoriteMatch._id,
        },
        awaitRefetchQueries: true,
        refetchQueries: [{ query: GetUserMinimalFavoritesDocument }],
      })
        .then(() => {
          setShouldRefetchFavorites(true);
          toaster.positive(t('removeFavoriteSucess'));
        })
        .catch(() => {
          toaster.negative(t('itemFavoritedError'));
        });
      return;
    }

    createMinimalFavorite({
      variables: {
        input: {
          entries: [
            {
              id: cartEntry.id,
              quantity: cartEntry.quantity,
              menuSelections: mapMenuSelections(cartEntry.menuSelections),
            },
          ],
          name: cartEntry.name,
          ref: getMinimalCartEntryRef(cartEntry),
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        { query: GetUserFavoritesRefsDocument },
        { query: GetUserMinimalFavoritesDocument },
      ],
    })
      .then(() => {
        setShouldRefetchFavorites(true);
        toaster.positive(t('itemFavorited'));
      })
      .catch(() => {
        toaster.negative(t('itemFavoritedError'));
      });
  };

  return (
    <IconButton
      variant="ghost"
      onPress={onPress}
      testID="favorite-icon"
      disabled={loading}
      _web={{
        cursor: 'default',
      }}
      icon={<FavoriteIcon isFavorite={isFavorite} disabled={loading} loading={loading} />}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ checked: true }}
    />
  );
};
